import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { DEFAULT_ICON_CLASS_NAME } from '../../theme/constants/icon';
import DocumentUploadLabel from '../document-upload';
import ClearIconButton from '../icon-buttons/ClearIconButton';

const getStatusIcon = (hasInvoiceDocuments, loading, isMobile) => {
  if (isMobile) {
    return null;
  }
  if (hasInvoiceDocuments) {
    return <CheckCircleOutlineOutlinedIcon sx={{ color: 'success.main' }} />;
  }
  if (loading) {
    return <CircularProgress size={24} className={DEFAULT_ICON_CLASS_NAME} />;
  }
  return <UploadFileOutlinedIcon className={DEFAULT_ICON_CLASS_NAME} />;
};

const UploadDocumentStatusButton = ({
  hasInvoiceDocuments,
  file,
  documentName,
  uploadFile,
  onCancel,
  loading,
  initialText,
  finalText,
  uploadedText,
}) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (file) {
      uploadFile();
    }
  }, [file, uploadFile]);

  const labelFileName = useMemo(() => {
    if (hasInvoiceDocuments) {
      return documentName || hasInvoiceDocuments;
    }
    return file?.name;
  }, [documentName, file, hasInvoiceDocuments]);

  const showClearButton = Boolean(file || hasInvoiceDocuments);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <IconButton
        component="span"
        size="large"
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          borderRadius: 2,
          p: { xs: 0, md: 'auto' },
          height: { xs: 35, md: 55 },
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: 'transparent',
            borderRadius: '50%',
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1,
          }}
        >
          {getStatusIcon(hasInvoiceDocuments, loading, isMobile)}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <DocumentUploadLabel
            fileName={labelFileName}
            initialText={initialText}
            finalText={finalText}
            uploadedText={uploadedText}
            hasPersistedDocument={hasInvoiceDocuments}
            truncate
            variant="responsive"
          />
          {showClearButton && (
            <ClearIconButton onClick={onCancel} />
          )}
        </Box>
      </IconButton>
    </Box>
  );
};

UploadDocumentStatusButton.propTypes = {
  hasInvoiceDocuments: PropTypes.bool.isRequired,
  documentName: PropTypes.string,
  file: PropTypes.shape({
    name: PropTypes.string,
  }),
  uploadFile: PropTypes.func,
  onCancel: PropTypes.func,
  loading: PropTypes.bool,
  initialText: PropTypes.string,
  finalText: PropTypes.string,
  uploadedText: PropTypes.string,
};

UploadDocumentStatusButton.defaultProps = {
  file: null,
  uploadFile: () => {},
  onCancel: () => {},
  loading: false,
  documentName: '',
  initialText: 'Cargar',
  finalText: ' archivo',
  uploadedText: null,
};

export default UploadDocumentStatusButton;
